import React from 'react';
import { Button, MenuItem, Stack, TextField } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import StarIcon from '@mui/icons-material/Star';
import type {
    TournamentScheduleFilterState,
    TournamentSchedulePreset,
    TournamentScheduleStatusFilter,
} from '../../../../Tournament/TournamentSchedule';
import {
    getDefaultTournamentScheduleFilterState,
    getTournamentSchedulePresetState,
} from '../../../../Tournament/TournamentSchedule';

type TournamentScheduleFilterBarProps = {
    value: TournamentScheduleFilterState;
    onChange: (value: TournamentScheduleFilterState) => void;
    compact?: boolean;
    showPresets?: boolean;
    showReset?: boolean;
};

const statusOptions: Array<{ value: TournamentScheduleStatusFilter; label: string }> = [
    { value: 'ALL', label: 'All statuses' },
    { value: 'REGISTRATION', label: 'Registration' },
    { value: 'LATE_REGISTRATION', label: 'Late registration' },
    { value: 'RUNNING', label: 'Running' },
    { value: 'PAUSED', label: 'Paused' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' },
];

const presetButtons: Array<{ value: Exclude<TournamentSchedulePreset, 'CUSTOM'>; label: string; Icon: typeof BoltIcon }> = [
    { value: 'CURRENT', label: 'Current', Icon: BoltIcon },
    { value: 'OPEN', label: 'Open', Icon: PlayCircleFilledWhiteIcon },
    { value: 'UPCOMING_SATELLITES', label: 'Satellites', Icon: SatelliteAltIcon },
    { value: 'UPCOMING_TICKET_ENTRY', label: 'Ticket Entry', Icon: ConfirmationNumberIcon },
    { value: 'EVENTS', label: 'Events', Icon: StarIcon },
];

// Shared control height keeps every field, the segmented presets and the reset
// button sitting on the same baseline so the desktop toolbar reads as one strip.
const CONTROL_HEIGHT = 36;
const ACCENT = '#F93751';
const ACCENT_HOVER = '#D71149';

type PresetGroupProps = {
    value: TournamentScheduleFilterState;
    onChange: (value: TournamentScheduleFilterState) => void;
    compact?: boolean;
};

/**
 * Segmented Current / Open / Satellites / Ticket Entry / Events control. Exported so the desktop
 * toolbar can centre it on its own while the rest of the filter bar stays left.
 * Current intentionally mirrors Reset: it returns to today's default lobby view.
 * The other presets keep the currently selected date out of the global preset flow.
 */
export const TournamentSchedulePresetGroup: React.FC<PresetGroupProps> = ({ value, onChange, compact = false }) => {
    const selectPreset = (preset: Exclude<TournamentSchedulePreset, 'CUSTOM'>) => {
        if (preset === 'CURRENT') {
            onChange(getDefaultTournamentScheduleFilterState());
            return;
        }

        if (preset === 'OPEN') {
            onChange(getTournamentSchedulePresetState(preset, getDefaultTournamentScheduleFilterState().date));
            return;
        }

        onChange(getTournamentSchedulePresetState(preset, value.date));
    };

    return (
        <Stack
            direction="row"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: compact ? 'auto' : CONTROL_HEIGHT,
                minHeight: CONTROL_HEIGHT,
                width: compact ? '100%' : 'auto',
                p: '3px',
                gap: '2px',
                flexWrap: compact ? 'wrap' : 'nowrap',
                boxSizing: 'border-box',
                backgroundColor: '#1B1C22',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: '9px',
            }}
        >
            {presetButtons.map(({ value: presetValue, label, Icon }) => {
                const active = value.preset === presetValue;
                return (
                    <Button
                        key={presetValue}
                        disableElevation
                        disableRipple
                        variant="text"
                        startIcon={<Icon />}
                        onClick={() => selectPreset(presetValue)}
                        sx={{
                            flex: compact ? '1 1 calc(50% - 2px)' : 'initial',
                            height: compact ? 30 : '100%',
                            minWidth: 0,
                            px: compact ? 0.75 : 1.5,
                            fontSize: compact ? '11px' : '12px',
                            fontWeight: 600,
                            lineHeight: 1,
                            letterSpacing: '0.01em',
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            borderRadius: '6px',
                            color: active ? '#FFFFFF' : '#98A2B3',
                            backgroundColor: active ? ACCENT : 'transparent',
                            transition: 'background-color 140ms ease, color 140ms ease',
                            '& .MuiButton-startIcon': {
                                mr: 0.6,
                                ml: 0,
                                '& > svg': { fontSize: compact ? 14 : 16 },
                            },
                            '&:hover': {
                                backgroundColor: active ? ACCENT_HOVER : 'rgba(255,255,255,0.07)',
                                color: '#FFFFFF',
                            },
                        }}
                    >
                        {label}
                    </Button>
                );
            })}
        </Stack>
    );
};

const TournamentScheduleFilterBar: React.FC<TournamentScheduleFilterBarProps> = ({
    value,
    onChange,
    compact = false,
    showPresets = false,
    showReset = true,
}) => {
    const update = <K extends keyof TournamentScheduleFilterState>(key: K, nextValue: TournamentScheduleFilterState[K]) => {
        onChange({
            ...value,
            [key]: nextValue,
            preset: 'CUSTOM',
        });
    };

    const fieldSx = {
        '& .MuiInputBase-root': {
            height: CONTROL_HEIGHT,
            backgroundColor: '#24252D',
            color: '#FFFFFF',
            borderRadius: '8px',
            fontSize: '13px',
        },
        '& .MuiOutlinedInput-input': {
            paddingTop: '8px',
            paddingBottom: '8px',
        },
        '& .MuiInputLabel-root': {
            color: '#8A93A3',
            fontSize: '13px',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '12px',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: ACCENT,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.10)',
            transition: 'border-color 140ms ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.24)',
        },
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: ACCENT,
            borderWidth: '1px',
        },
        '& .MuiSvgIcon-root': {
            color: '#8A93A3',
        },
    };

    // Force the native date control (text + calendar popup) into dark mode and
    // tint the picker indicator so it matches the rest of the app.
    const dateFieldSx = {
        ...fieldSx,
        '& input[type="date"]': {
            colorScheme: 'dark',
        },
        '& input[type="date"]::-webkit-calendar-picker-indicator': {
            filter: 'invert(0.65) brightness(1.1)',
            opacity: 0.75,
            cursor: 'pointer',
            transition: 'opacity 140ms ease',
        },
        '& input[type="date"]:hover::-webkit-calendar-picker-indicator': {
            opacity: 1,
        },
    };

    return (
        <Stack
            direction={compact ? 'column' : 'row'}
            spacing={compact ? 1 : 1}
            sx={{
                width: compact ? '100%' : 'auto',
                flexShrink: 0,
                alignItems: compact ? 'stretch' : 'center',
            }}
        >
            <TextField
                label="Search"
                size="small"
                value={value.nameContains}
                onChange={(event) => update('nameContains', event.target.value)}
                sx={{ ...fieldSx, minWidth: compact ? '100%' : 150, width: compact ? '100%' : 156 }}
            />
            <TextField
                label="Date"
                type="date"
                size="small"
                value={value.date}
                onChange={(event) => {
                    if (event.target.value) update('date', event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ ...dateFieldSx, minWidth: compact ? '100%' : 142, width: compact ? '100%' : 142 }}
            />
            <TextField
                select
                label="Status"
                size="small"
                value={value.status}
                onChange={(event) => update('status', event.target.value as TournamentScheduleStatusFilter)}
                sx={{ ...fieldSx, minWidth: compact ? '100%' : 148, width: compact ? '100%' : 148 }}
            >
                {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
            </TextField>
            {showPresets && (
                <TournamentSchedulePresetGroup value={value} onChange={onChange} compact={compact} />
            )}
            {showReset && (
                <Button
                    variant="outlined"
                    onClick={() => onChange(getDefaultTournamentScheduleFilterState())}
                    sx={{
                        height: CONTROL_HEIGHT,
                        color: '#C7CEDA',
                        borderColor: 'rgba(255,255,255,0.16)',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        minWidth: compact ? '100%' : 82,
                        '&:hover': {
                            borderColor: 'rgba(255,255,255,0.32)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                    }}
                >
                    Reset
                </Button>
            )}
        </Stack>
    );
};

export default TournamentScheduleFilterBar;
